import { ChevronDown, Layers } from 'lucide-react';

import { dueLabel, ladderBreakdown, type GroupedItems } from './practiceMeta';
import { PracticeRow } from './PracticeRow';

/**
 * One authored set — a week's problems, say — as a single card: label,
 * progress, due date and the difficulty ladder, with the individual items a
 * disclosure away.
 *
 * A native <details> is used deliberately: it keeps the whole set reachable by
 * keyboard and to a screen reader with no JS state, and it collapses to a
 * single tap target on a phone.
 */
export function PracticeGroupCard({
  grouped,
  batchId,
  defaultOpen = false,
}: {
  grouped: GroupedItems;
  batchId: string;
  defaultOpen?: boolean;
}) {
  const { group, items } = grouped;
  // Counted from the items actually in hand rather than trusting group.total:
  // if the two ever disagree the bar must match the rows underneath it.
  const total = items.length;
  const completed = items.filter((i) => i.submitted).length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const due = dueLabel(group.due_date);
  const ladder = ladderBreakdown(items);
  const done = total > 0 && completed === total;

  return (
    <details
      open={defaultOpen}
      className="group overflow-hidden rounded-[20px] border border-[#ececec] bg-white shadow-[0px_1px_1.5px_rgba(0,0,0,0.1),0px_1px_1px_rgba(0,0,0,0.1)]"
    >
      <summary className="flex cursor-pointer list-none items-start gap-3 p-4 transition-colors hover:bg-[#fafafa] sm:items-center sm:gap-4 sm:p-5 [&::-webkit-details-marker]:hidden">
        <span
          className="flex size-11 shrink-0 items-center justify-center rounded-xl"
          style={{ background: done ? '#dcfce7' : '#f5f3ff' }}
        >
          <Layers className="size-5" style={{ color: done ? '#16a34a' : '#8b5cf6' }} />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <p className="min-w-0 truncate text-[15px] font-bold text-[#111827] sm:text-base">{group.label}</p>
            <span className="shrink-0 rounded-full bg-[#f3f4f6] px-2 py-0.5 text-[11px] font-semibold text-[#6b7280]">
              {completed}/{total}
            </span>
            {due && !done && (
              <span className={`shrink-0 text-[12px] font-semibold ${due.urgent ? 'text-[#ff5a1f]' : 'text-[#9ca3af]'}`}>
                {due.text}
              </span>
            )}
            {done && <span className="shrink-0 text-[12px] font-semibold text-[#16a34a]">Completed</span>}
          </div>

          {/* Progress */}
          <div className="mt-2 flex items-center gap-2">
            <span
              className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-[#ede9fe]"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${group.label} progress`}
            >
              <span
                className="block h-full rounded-full"
                style={{
                  width: `${pct}%`,
                  background: done ? '#16a34a' : 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                }}
              />
            </span>
            <span className="shrink-0 text-[12px] font-medium tabular-nums text-[#6b7280]">{pct}%</span>
          </div>

          {/* The authored ladder — 3 warm-up · 4 core · 2 stretch · 1 debug —
              or, for a set that predates rungs, its difficulty tally. */}
          {ladder.length > 0 && (
            <ul
              className="mt-2 flex flex-wrap items-center gap-1.5"
              aria-label={`${group.label} ladder: ${ladder.map((r) => `${r.count} ${r.label}`).join(', ')}`}
            >
              {ladder.map((rung) => (
                <li
                  key={rung.key}
                  className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                  style={{ background: rung.bg, color: rung.fg }}
                >
                  {rung.count} {rung.label}
                </li>
              ))}
            </ul>
          )}
        </div>

        <ChevronDown className="mt-1 size-4 shrink-0 text-[#d1d5db] transition-transform group-open:rotate-180 sm:mt-0" />
      </summary>

      <div className="divide-y divide-[#f3f4f6] border-t border-[#f3f4f6]">
        {items.map((item) => (
          <PracticeRow key={item.id} item={item} batchId={batchId} />
        ))}
      </div>
    </details>
  );
}
